/* STORE (state + hành động) — dùng Context để mọi màn hình truy cập */
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { App as AntApp } from 'antd';
import { toast } from 'react-toastify';
import {
    CODES, CUS, INIT_TX, KW, MAXT, MT, PROFILES, SVC, TPL, svcOf,
} from './Data';
import { fmt, money } from './UI';
import type {
    BgState, CallState, Customer, DockState, MenuKey, Msg, MsgState, Profile, Task,
} from './Types';

export const defSub = (c: Customer) => (c.f === 'moi' ? 'nhu' : c.f === 'lh' ? 'lich' : 'cham');
const msDefault = (ma: string, MT: Record<string, Msg[]>): MsgState => ({ chan: 'Zalo', th: MT[ma] ?? [], tpl: '', draft: '', code: null, saved: false });
const bgDefault = (c: Customer): BgState => ({
    mang: c.mang, dv: SVC[c.mang]?.[0]?.[0] ?? '', ct: '', giam: '', lydo: '', status: 'Nháp',
    han: '31/10/2026', mucdich: 'Giữ lịch', tienIn: '', coc: null,
});
const allCodes = CODES.flatMap((g) => g.items);
export const flaggedMsgs = (th: Msg[]) => th.some((m) => m.from === 'k' && KW.some((w) => m.text.toLowerCase().includes(w)));

export function useStore() {
    const [menu, setMenu] = useState<MenuKey>('td');
    const [profiles, setProfiles] = useState<Record<string, Profile>>(PROFILES);
    const [dock, setDock] = useState<DockState>({ open: false, tabs: [], act: null });
    const [drw, setDrw] = useState(42);
    const [dmsg, setDmsg] = useState<{ t: string; ok: boolean } | null>(null);
    const [call, setCall] = useState<CallState | null>(null);
    const [ms, setMs] = useState<Record<string, MsgState>>({});
    const [bgs, setBgs] = useState<Record<string, BgState>>({});
    const [tx, setTx] = useState<Task[]>(INIT_TX);
    const [agent, setAgent] = useState({ st: 'Sẵn sàng', rs: '' });
    const [modal, setModal] = useState<null | { kind: 'task' | 'flag'; ma?: string }>(null);

    const dockRef = useRef(dock);
    const callRef = useRef(call);
    callRef.current = call;
    const commitDock = (nd: DockState) => { dockRef.current = nd; setDock(nd); };

    /* ---- ngăn hồ sơ ---- */
    const openTab = (ma: string, sub?: string) => {
        const d = dockRef.current;
        let tabs = d.tabs;
        const ex = tabs.find((t) => t.ma === ma);
        if (!ex) {
            if (tabs.length >= MAXT) {
                const old = tabs.find((t) => callRef.current?.ma !== t.ma);
                if (old) { tabs = tabs.filter((t) => t !== old); toast.info(`Đã đóng tab cũ nhất — tối đa ${MAXT} tab`); }
            }
            tabs = [...tabs, { ma, sub: sub || defSub(CUS[ma]) }];
        } else if (sub) tabs = tabs.map((t) => (t.ma === ma ? { ...t, sub } : t));
        commitDock({ open: true, tabs, act: ma });
        setDmsg(null);
    };
    const setSub = (ma: string, sub: string) => {
        const d = dockRef.current;
        commitDock({ ...d, tabs: d.tabs.map((t) => (t.ma === ma ? { ...t, sub } : t)) });
        setDmsg(null);
    };
    const activate = (ma: string) => { commitDock({ ...dockRef.current, act: ma }); setDmsg(null); };
    const closeTab = (ma: string) => {
        const d = dockRef.current;
        if (callRef.current?.ma === ma) {
            commitDock({ ...d, act: ma, tabs: d.tabs.map((t) => (t.ma === ma ? { ...t, sub: 'call' } : t)) });
            setDmsg({ t: 'Chưa lưu mã kết quả — không đóng được tab đang gọi (R01).', ok: false });
            return;
        }
        const tabs = d.tabs.filter((t) => t.ma !== ma);
        commitDock({ open: tabs.length > 0, tabs, act: d.act === ma ? (tabs.length ? tabs[tabs.length - 1].ma : null) : d.act });
        setDmsg(null);
    };
    const escClose = () => {
        const d = dockRef.current;
        if (!d.open) return;
        if (callRef.current) {
            commitDock({ ...d, act: callRef.current.ma });
            setDmsg({ t: 'Đang có cuộc gọi chưa lưu mã kết quả — không đóng ngăn được (R01).', ok: false });
            return;
        }
        commitDock({ ...d, open: false });
    };

    /* ---- hồ sơ: dòng thời gian, cờ, cờ y khoa ---- */
    const addTL = (ma: string, t: string) =>
        setProfiles((p) => ({ ...p, [ma]: { ...p[ma], tl: [['Vừa xong', t], ...p[ma].tl] } }));
    const addFlag = (ma: string, f: string, cls: 'info' | '' = '') =>
        setProfiles((p) => (p[ma].flags.some((x) => x[0].startsWith(f)) ? p : { ...p, [ma]: { ...p[ma], flags: [...p[ma].flags, [f, cls]] } }));
    const setCS = (ma: string, k: string, v: string) =>
        setProfiles((p) => ({
            ...p,
            [ma]: { ...p[ma], cs: p[ma].cs.map(([key, val]) => (key === k ? [key, (val === 'Không' || val === 'Chưa có' ? '' : `${val} · `) + v] : [key, val]) as [string, string]) },
        }));
    const noCall = (ma: string) => !!CUS[ma].khonggoi || !!profiles[ma]?.flags.some((f) => f[0].startsWith('Không gọi'));

    /* ---- cuộc gọi ---- */
    const startCall = (ma: string, incoming = false) => {
        const cur = callRef.current;
        if (cur) { toast.warning(`Đang có cuộc gọi với ${CUS[cur.ma].ten} — lưu mã kết quả trước (R01)`); return; }
        if (!incoming && noCall(ma)) { toast.warning('Khách có cờ Không gọi — chỉ nhắn Zalo (R19)'); return; }
        if (!incoming && CUS[ma].kenh === 'Chỉ nhắn tin') { toast.warning('Khách chỉ muốn nhắn tin — dùng Zalo'); return; }
        const cs: CallState = { ma, incoming, sec: 0, phase: 'talk', hold: false, acw: 90, code: null, f: {} };
        callRef.current = cs;
        setCall(cs);
        openTab(ma, 'call');
    };
    const inCall = !!call;
    useEffect(() => {
        if (!inCall) return undefined;
        const id = setInterval(() => setCall((c) => (c ? (c.phase === 'talk' ? { ...c, sec: c.sec + 1 } : { ...c, acw: c.acw - 1 }) : c)), 1000);
        return () => clearInterval(id);
    }, [inCall]);
    const hangup = () => setCall((c) => (c ? { ...c, phase: 'acw', hold: false } : c));
    const toggleHold = () => setCall((c) => (c ? { ...c, hold: !c.hold } : c));
    const setCallCode = (code: string) => setCall((c) => (c ? { ...c, code } : c));
    const setCallField = (k: string, v: string) => setCall((c) => (c ? { ...c, f: { ...c.f, [k]: v } } : c));
    const gotoCall = () => {
        const c = callRef.current;
        if (!c) return;
        const d = dockRef.current;
        commitDock({ open: true, act: c.ma, tabs: d.tabs.map((t) => (t.ma === c.ma ? { ...t, sub: 'call' } : t)) });
    };
    const saveCall = (next?: boolean) => {
        const c = callRef.current;
        if (!c) return;
        const { f, code: k } = c;
        let e: string | null = null;
        if (!k) e = 'Chưa chọn mã kết quả. Không thể lưu và đóng cuộc gọi (R01).';
        else if (k === 'KN-01' && (!f.lhNgay || !f.lhCn || !f.lhMang)) e = 'Đặt lịch phải có ngày giờ, chi nhánh, mảng (R02).';
        else if (k === 'KN-02' && !f.hgNgay) e = 'Hẹn gọi lại phải có ngày giờ (R02).';
        if (e || !k) { setDmsg({ t: e ?? '', ok: false }); return; }
        const extra: Record<string, string> = { 'KN-05': ' Khách ở lại danh sách Telesales (R20).', 'KN-07': ' Đã tạo phiếu Chuyển bác sĩ (R04).' };
        const label = allCodes.find((x) => x.code === k)?.label ?? '';
        addTL(c.ma, `${c.incoming ? 'Gọi đến' : 'Gọi đi'} · NV-03 · ${fmt(c.sec)} · **${k} ${label}**${f.note ? ` · “${f.note}”` : ''}`);
        callRef.current = null;
        setCall(null);
        setSub(c.ma, 'cham');
        setDmsg({ t: `Đã lưu ${k}. Cập nhật mã trạng thái theo mục 4 (R03).${extra[k] ?? ''}${next ? ' Mở việc tiếp theo trong hàng đợi.' : ''}`, ok: true });
    };

    /* ---- nhắn tin ---- */
    const msOf = (ma: string) => ms[ma] ?? msDefault(ma, MT);
    const mutMs = (ma: string, fn: (s: MsgState) => MsgState) => setMs((m) => ({ ...m, [ma]: fn(m[ma] ?? msDefault(ma, MT)) }));
    const fillTpl = (ma: string, id: string, bgArg?: BgState) => {
        const c = CUS[ma];
        const p = profiles[ma];
        const t = TPL.find((x) => x[0] === id)?.[2];
        if (!t || !p) return '';
        const bg = bgArg ?? bgs[ma];
        const X = p.xung.charAt(0).toUpperCase() + p.xung.slice(1);
        const dv = bg?.dv ? bg.dv : c.dvg && c.dvg !== '—' && c.dvg !== 'Chưa dùng' ? c.dvg.split(' · ')[0] : 'dịch vụ đã làm';
        return t
            .replace(/\{xung\}/g, p.xung).replace(/\{Xung\}/g, X)
            .replace(/\{ten\}/g, c.ten.replace(/^(Anh|Chị) /, ''))
            .replace(/\{dv\}/g, dv)
            .replace(/\{gia\}/g, bg?.dv ? money(svcOf(bg).gia) : '[giá từ]')
            .replace(/\{han\}/g, bg?.han || '31/10/2026')
            .replace(/\{ngay\}/g, '17/10 09:30');
    };
    const openMsg = (ma: string, chan?: string) => {
        mutMs(ma, (s) => ({ ...s, chan: chan ?? s.chan }));
        openTab(ma, 'msg');
    };
    const pickTpl = (ma: string, id: string) => mutMs(ma, (s) => ({ ...s, tpl: id, draft: id ? fillTpl(ma, id) : '' }));
    const setDraft = (ma: string, draft: string) => mutMs(ma, (s) => ({ ...s, draft }));
    const setMsgCode = (ma: string, code: string) => mutMs(ma, (s) => ({ ...s, code }));
    const sendMsg = (ma: string) => {
        const s = msOf(ma);
        if (!s.draft.trim()) { setDmsg({ t: 'Chưa có nội dung tin.', ok: false }); return; }
        const id = Date.now();
        const text = s.draft;
        mutMs(ma, (cur) => ({ ...cur, th: [...cur.th, { id, from: 'n', time: 'Vừa xong', text, status: 'Đã gửi' }], draft: '', tpl: '' }));
        setBgs((b) => (b[ma] && b[ma].status === 'Nháp' && text.includes('báo giá') ? { ...b, [ma]: { ...b[ma], status: 'Đã gửi' } } : b));
        setDmsg(null);
        window.setTimeout(() => mutMs(ma, (cur) => ({ ...cur, th: cur.th.map((m) => (m.id === id ? { ...m, status: 'Đã nhận' } : m)) })), 1500);
    };
    const saveMsg = (ma: string) => {
        const s = msOf(ma);
        if (!s.code) { setDmsg({ t: 'Chưa chọn mã kết quả cho lượt nhắn (R24 · R01).', ok: false }); return; }
        mutMs(ma, (cur) => ({ ...cur, saved: true }));
        addTL(ma, `${s.chan} · lượt nhắn · mã **${s.code}**`);
        setDmsg({ t: `Đã lưu lượt nhắn ${s.code} · đồng bộ vào Lịch sử chạm.`, ok: true });
    };
    const saveImg = (ma: string, idx: number) => {
        const has = profiles[ma].dv.length > 0;
        const moc = has ? (flaggedMsgs(msOf(ma).th) ? 'Bất thường' : 'T+24h') : 'Trước tư vấn';
        mutMs(ma, (cur) => ({
            ...cur,
            th: cur.th.map((m, i) => (i === idx ? { ...m, note: `Đã lưu vào hồ sơ · gợi ý mốc: **${moc}** (tính từ ngày làm dịch vụ — R28)` } : m)),
        }));
    };

    /* ---- báo giá + cọc ---- */
    const bgOf = (ma: string) => bgs[ma] ?? bgDefault(CUS[ma]);
    const updBg = (ma: string, patch: Partial<BgState>) => setBgs((b) => ({ ...b, [ma]: { ...(b[ma] ?? bgDefault(CUS[ma])), ...patch } }));
    const bgAct = (ma: string, a: 'duyet' | 'gui' | 'guiw' | 'qr' | 'bl' | 'cs') => {
        const b = bgOf(ma);
        if (a === 'duyet') { updBg(ma, { status: 'Chờ duyệt' }); setDmsg({ t: 'Đã gửi xin duyệt Trưởng phòng Telesales — báo giá ở trạng thái Chờ duyệt.', ok: true }); return; }
        if (a === 'gui' || a === 'guiw') {
            updBg(ma, { status: 'Đã gửi' });
            mutMs(ma, (s) => ({ ...s, chan: a === 'gui' ? 'Zalo' : 'WhatsApp', tpl: 'bg', draft: fillTpl(ma, 'bg', { ...b, status: 'Đã gửi' }) }));
            openTab(ma, 'msg');
            return;
        }
        if (a === 'qr') updBg(ma, { coc: { tien: b.tienIn || '2.000.000 đ (minh hoạ)', mucd: b.mucdich, st: 'Chờ chuyển khoản' } });
        if (a === 'bl' && b.coc) updBg(ma, { coc: { ...b.coc, st: 'Chờ đối soát', bl: true } });
        if (a === 'cs') { updBg(ma, { status: 'Đã chuyển cơ sở' }); setDmsg({ t: 'Báo giá chuyển Tư vấn cơ sở; chờ nối phiếu báo giá chính thức (R33).', ok: true }); }
    };

    /* ---- việc + cờ tạo tay ---- */
    const submitTask = (v: { ma: string; loai: string; han: string; nv: string }) => {
        const src = `Tạo tay · NV-03 → ${v.nv}`;
        setTx((t) => [{ ma: v.ma, loai: `${v.loai} — tạo tay`, han: v.han, late: false, src }, ...t]);
        addTL(v.ma, `Tạo việc tay: ${v.loai} · ${src}`);
        toast.success(`Đã tạo việc cho ${v.ma}`);
        setModal(null);
    };
    const submitFlag = (v: { ma: string; flag: string }) => {
        if (v.flag === 'Biến chứng') {
            addFlag(v.ma, 'Biến chứng');
            setCS(v.ma, 'Phiếu chuyển bác sĩ', '**BS-0210** · mới · !!chờ bác sĩ nhận!!');
            setCS(v.ma, 'Cờ cần bác sĩ xem trước', 'Có');
            addTL(v.ma, 'Gắn cờ **Biến chứng** · tạo phiếu chuyển bác sĩ · việc chuyển sang phòng CSKH (R04)');
        } else if (v.flag === 'Khiếu nại') {
            addFlag(v.ma, 'Khiếu nại');
            setCS(v.ma, 'Khiếu nại', '**KN-0415** · mới · đang mở');
            addTL(v.ma, 'Gắn cờ **Khiếu nại** · tạo phiếu khiếu nại · việc chuyển sang phòng CSKH');
        } else {
            addFlag(v.ma, v.flag, v.flag === 'Ở nước ngoài' ? 'info' : '');
            addTL(v.ma, `Gắn cờ **${v.flag}** · tạo tay · NV-03`);
        }
        setModal(null);
    };

    return { 
        menu, setMenu, profiles, dock, drw, setDrw, dmsg, setDmsg, call, ms, tx, agent, setAgent, modal, setModal,
        openTab, setSub, activate, closeTab, escClose, noCall,
        startCall, hangup, toggleHold, setCallCode, setCallField, gotoCall, saveCall,
        msOf, openMsg, pickTpl, setDraft, setMsgCode, sendMsg, saveMsg, saveImg,
        bgOf, updBg, bgAct, submitTask, submitFlag,
    };
}
export type Store = ReturnType<typeof useStore>;
export const StoreCtx = createContext<Store>(null as unknown as Store);
export const useS = () => useContext(StoreCtx);

/** Hook media query (dock hồ sơ chỉ gắn cạnh khi ≥ xl) */
export function useMedia(q: string) {
    const [m, setM] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(q).matches : false));
    useEffect(() => {
        const mq = window.matchMedia(q);
        const h = () => setM(mq.matches);
        h();
        mq.addEventListener('change', h);
        return () => mq.removeEventListener('change', h);
    }, [q]);
    return m;
}